import i18n from "@/plugins/i18n";

const ssoProps = {
	google: {
		provider: "Google",
		isProvisioned: false,
		redirectPath: "/sso",
		type: "code",
	},
	saml: {
		type: "code",
		provider: "idpProvider",
		isProvisioned: true,
		email: "identifier",
		redirectPath: "/sso",
	},
	authSwitchSso: {
		provider: "Google",
		isProvisioned: false,
		redirectPath: "/auth-switch-sso",
		type: "token",
	},
	okta: {
		provider: "okta",
		isProvisioned: false,
		redirectPath: "/auth-switch-sso",
		type: "token",
	},
};

function ssoPropConstructor(buttonId) {
	return {
		provider: ssoProps[buttonId]?.provider,
		email: ssoProps[buttonId]?.email || "",
		redirectPath: ssoProps[buttonId]?.redirectPath || "/sso",
		oAuthState: ssoProps[buttonId]?.oAuthState || false,
		type: ssoProps[buttonId]?.type || "code",
		isProvisioned: ssoProps[buttonId]?.isProvisioned || false,
		buttonType: ssoProps[buttonId]?.buttonType || "primary",
	};
}

export default function ({ globals }) {
	return {
		namespaced: true,
		state: {
			cognitoParams: undefined,
			ssoParams: null,
			idpProvider: null,
			isSamlLoginLoading: false,
			hideIdentifierOnLoad: false,
		},
		mutations: {
			SET_COGNITO_PARAMS(state, value) {
				state.cognitoParams = value;
			},
			SET_IDP_PROVIDER(state, value) {
				state.idpProvider = value;
			},
			SET_SAML_LOADING_STATE(state, value) {
				state.isSamlLoginLoading = value;
			},
			SET_HIDE_IDENTIFIER_ON_LOAD(state, value) {
				state.hideIdentifierOnLoad = value;
			},
		},
		actions: {
			async initCognitoUserPool({ state, commit, dispatch }) {
				if (typeof state.cognitoParams === "object") {
					return;
				}
				if (state.cognitoParams === "loading") {
					while (typeof state.cognitoParams !== "object") {
						// eslint-disable-next-line no-await-in-loop
						await new Promise((resolve) => {
							setTimeout(resolve, 200);
						});
					}
					return;
				}
				try {
					commit("SET_COGNITO_PARAMS", "loading");
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const usersConfigRequest = await fetch(`${globalConfig.backends.users}/configs`, {
						method: "GET",
					});
					const { result, error } = await usersConfigRequest.json();

					if (error) throw new Error(error.code);

					commit("SET_COGNITO_PARAMS", result);
				} catch (error) {
					const translationExists = i18n.te(`sso.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`sso.${code}`),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });
				}
			},
			async setIdpString({ commit, dispatch, rootState }) {
				try {
					const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
					const idpRequest = await fetch(`${globalConfig.backends.users}/emails/${rootState.login.identifier}/provider`, {
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					});

					const { result, error = null } = await idpRequest.json();

					if (error) throw new Error(error.code);

					commit("SET_IDP_PROVIDER", result.providerName);
				} catch (error) {
					const translationExists = i18n.te(`sso.${error.message}`);
					const code = translationExists ? error.message : "networkError";

					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code,
						source: "backend",
						stack: error,
						notification: i18n.t(`sso.${code}`),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });
				}
			},
			async initiateOAuth(context, invitationHash) {
				const globalConfig = await globals.getAsyncGlobalVariable("environmentConfig");
				const response = await fetch(`${globalConfig.backends.users}/oauth/state?invitationHash=${invitationHash}`, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				});
				return await response.json();
			},
			async signIn({ state, dispatch, commit, rootState }, buttonId) {
				commit("SET_SAML_LOADING_STATE", true);
				const actualSsoProps = ssoPropConstructor(buttonId);
				await dispatch("initCognitoUserPool");
				const redirectUri = `${window.location.origin}${actualSsoProps.redirectPath}`;
				// Get this info from the backend and not amplify
				let provider;

				try {
					if (actualSsoProps.isProvisioned) {
						if (!state.idpProvider) {
							await dispatch("setIdpString");
						} else {
							if (state.idpProvider.length !== 24) {
								throw new Error("invalidId");
							}
						}
						provider = state.idpProvider;
					} else {
						provider = actualSsoProps.provider;
					}

					const invitationHash = rootState.register?.isInvitation && rootState.register?.invitationData?.invitationHash
						? rootState.register?.invitationData.invitationHash
						: "";

					const { result: { oAuthState } } = await dispatch("initiateOAuth", invitationHash);

					if (!oAuthState) throw new Error("oAuthInitFailed");

					const customParams = {
						oAuthState,
						registrationToken: rootState.queryParams?.token || "",
					};
					const customState = encodeURIComponent(JSON.stringify(customParams));

					window.location = `
						${state.cognitoParams.domain}/oauth2/authorize
						?identity_provider=${provider}
						&redirect_uri=${redirectUri}
						&response_type=${actualSsoProps.type}
						&client_id=${state.cognitoParams.clientId}
						&scope=aws.cognito.signin.user.admin email openid profile
						${`&state=${customState}`}
					`;
				} catch (error) {
					dispatch("logger/logError", {
						id: Date.now(),
						handled: true,
						code: error.message,
						source: "backend",
						stack: error,
						notification: i18n.t(`sso.${error.message}`),
						target: {
							type: "flashToast",
							timeout: 4000,
						},
					}, { root: true });
					commit("SET_IDP_PROVIDER", "");
					commit("SET_SAML_LOADING_STATE", false);
					commit("SET_HIDE_IDENTIFIER_ON_LOAD", false);
				}
			},
			displayErrorOnSSO({ dispatch }, error) {
				const translationExists = i18n.te(`sso.${error.message}`);
				dispatch("logger/logError", {
					id: Date.now(),
					handled: true,
					code: error.message,
					source: "backend",
					stack: error,
					notification: translationExists ? i18n.t(`sso.${error.message}`) : error.message,
					target: {
						uid: "ssoRegistrationError",
						type: "flagToast",
					},
				}, { root: true });
			},
		},
	};
}
